import React, { useEffect, useState, useRef, MutableRefObject, useMemo, useCallback } from 'react';

import { usePageContext } from '~components/PageDataProvider/PageDataProvider';
import { filterKeyPress, generalRedirect, sendHotjarEvent, setCookie } from '~globals/common';
// import { WpCategoryPageContext, WpPostPageContext } from '~globals/interfaces';
import { fireTrackEvent } from '~globals/tracking';
import { patchSession } from '~globals/user';
import useAllianceId from '~hooks/useAllianceId';
import { useAppDispatch, useAppSelector } from '~hooks/useRedux';
import {
    updateGeoLocation,
    updateCounty,
    clearGeoLocation,
    updateFilter,
    updateMultiCountyList,
} from '~redux/Actions/GlobalActions';

import Form from './FormLayout';
import { getLocationByZip } from './util';

type Props = {
    inputLabel?: string;
    buttonTitle?: string;
    buttonTitleMobile?: string | null;
    buttonTitleMobileSuperSmall?: string | null;
    buttonWaLink: string;
    moreInfo?: boolean;
    opts?: object;
    qaValue: string;
    isZipLocked?: boolean;
    isGeneric?: boolean;
    redirect?: {
        toUrl?: string;
        withFastQuote?: boolean;
        withTool?: string;
        delay?: number;
        planTypeOverride?: PlansType | '';
    };
    customClasses?: {
        zipCodeInput?: string;
    };
    submitTrackingActionName?: string;
};

const ZipForm: React.FC<Props> = ({
    inputLabel = 'ZIP Code',
    buttonTitle = 'See Plans',
    buttonTitleMobile = null,
    buttonTitleMobileSuperSmall = null,
    buttonWaLink,
    moreInfo = true,
    qaValue,
    isZipLocked,
    isGeneric,
    opts = {},
    redirect: { delay = 0 } = {},
    customClasses = {},
    submitTrackingActionName,
}: Props) => {
    const pageData = usePageContext();
    const { pathname } = pageData.location;
    const isStatesPage = useMemo(() => pathname.includes('-health-insurance/'), [pathname]);
    const [zipValue, setZipValue] = useState('');
    const [countyValue, setCountyValue] = useState('');
    const [multiCountyList, setMultiCountyList] = useState([] as GeoLocation[]);
    const [showCountySelect, setShowCountySelect] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [hasZipError, setHasZipError] = useState(false);
    const [hasCountyError, setHasCountyError] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [didClickSubmit, setDidClickSubmit] = useState(false);

    const tooltip = useRef() as MutableRefObject<HTMLButtonElement> | null;
    const isSMB = ['/enroll/'].some((path) => pageData.location.pathname.includes(path));
    const allid = useAllianceId(isStatesPage);
    const planYear = useAppSelector((state) => state.global.planYear);
    const sessionCode = useAppSelector((state) => state.user.savedSession.sessionCode);
    const storeCounty = useAppSelector((state) => state.global.geoLocation.countyName);
    const storeZip = useAppSelector((state) => state.global.geoLocation.zip);
    const dispatch = useAppDispatch();
    const storeMultiCountyList = useAppSelector((state) => state.global.multiCountyList);

    useEffect(() => {
        if (storeZip && storeZip !== zipValue) {
            setZipValue(storeZip);

            // fetch the zip info if stored zip does not match the locations stored
            // (possible out of sync when coming from a saved session or the locations was changed in EHMP)
            if (!storeMultiCountyList?.find((location) => location.zip === storeZip)) {
                getCountyList(storeZip).catch((e) => {
                    console.error(e);
                });
            }
        }
    }, [storeZip]);

    useEffect(() => {
        if (storeCounty && storeCounty !== countyValue) {
            setCountyValue(storeCounty);
        }
    }, [storeCounty]);

    useEffect(() => {
        if (storeMultiCountyList !== multiCountyList) {
            setMultiCountyList(storeMultiCountyList);
        }
    }, [storeMultiCountyList]);

    useEffect(() => {
        if (hasCountyError) {
            setHasCountyError(false);
        }
        if (isZipLocked) {
            return;
        }
        if (countyValue && countyValue !== storeCounty) {
            if (multiCountyList) {
                const matchedCounty = multiCountyList.find((c) => c.countyName === countyValue);
                matchedCounty && dispatch(updateGeoLocation(matchedCounty));
            } else {
                dispatch(updateCounty(countyValue));
            }

            if (sessionCode) {
                patchSession(
                    { dispatch, updateGeoLocation },
                    {
                        allid,
                        sessionCode,
                        planYear,
                        census: { zip: zipValue, countyName: countyValue },
                    }
                ).catch((e) => {
                    console.error(e);
                });
            }
        }
    }, [countyValue]);

    useEffect(() => {
        setShowCountySelect(multiCountyList?.length > 1);
    }, [multiCountyList]);

    useEffect(() => {
        if (!isFetching && didClickSubmit) {
            setDidClickSubmit(false);
            if (zipValue.length !== 5) {
                setHasZipError(true);
                return;
            }
            if (countyValue === '') {
                setHasCountyError(true);
                return;
            }
            if (delay) {
                setTimeout(() => {
                    handleRedirect(zipValue);
                }, delay);
            } else {
                handleRedirect(zipValue);
            }
        }
    }, [isFetching, didClickSubmit, countyValue]);

    const handleRedirect = useCallback(
        (zip: string) => {
            sendHotjarEvent('SeePlans_click');
            dispatch(updateFilter(''));

            if (isStatesPage) {
                generalRedirect(
                    `${process.env.GATSBY_EHI_URL}individual-family-health-insurance/plans?&zipCode=${zip}`,
                    allid,
                    isSMB
                );
            } else {
                generalRedirect(
                    `${process.env.GATSBY_EHI_URL}smb-client/smbmedicalquote2.html?zip=${zip}`,
                    allid,
                    isSMB
                );
            }
        },
        [isStatesPage]
    );

    const handleZipInput = (e: React.FormEvent<HTMLInputElement>) => {
        const { value } = e.target as HTMLInputElement;
        if (value.match(/^[0-9]*$/)) {
            setZipValue(value);
            setCookie('sgZipCode', value);
            if (hasZipError) {
                setHasZipError(false);
            }
            if (hasCountyError) {
                setHasCountyError(false);
            }
            if (value.length === 5) {
                setIsFetching(true);
                getCountyList(value).catch((e) => {
                    console.error(e);
                });
            } else {
                setMultiCountyList([]);
            }
        }
        setDidClickSubmit(false);
    };

    const handleCountyChange = (e: React.FormEvent<HTMLSelectElement>) => {
        const { value } = e.target as HTMLSelectElement;
        setCountyValue(value);
        setDidClickSubmit(false);
    };

    const getCountyList = async (zip: string) => {
        const response = await getLocationByZip(zip);
        if (!response?.data?.areaByZipCode || response.data.areaByZipCode.hasError) {
            setCountyValue('');
            setHasZipError(true);
            dispatch(clearGeoLocation());
            dispatch(updateMultiCountyList([]));
            return;
        }
        const { locationList } = response.data.areaByZipCode;
        dispatch(updateMultiCountyList(locationList));
        if (locationList.length === 1) {
            const { countyName } = locationList[0];
            setCountyValue(countyName);
            setMultiCountyList([]);
            !isZipLocked && dispatch(updateGeoLocation(locationList[0]));
        } else {
            const matchedCounty = locationList.find((c) => c.countyName === countyValue);
            const currentCounty = matchedCounty || locationList[0];
            setCountyValue(currentCounty.countyName);
            setMultiCountyList(locationList);
            !isZipLocked && dispatch(updateGeoLocation(currentCounty));
        }
        setIsFetching(false);
    };

    const handleSubmit = (e: React.SyntheticEvent) => {
        e.preventDefault();
        setDidClickSubmit(true);
        if (submitTrackingActionName) {
            fireTrackEvent({
                page: { action: submitTrackingActionName },
                leadInfo: { zip: zipValue, county: countyValue },
            });
        } else {
            fireTrackEvent({
                page: { action: 'Quote form:Compare plans' },
                events: ['save_results'],
            });
        }
    };

    const props = {
        inputLabel,
        buttonTitle,
        buttonTitleMobile,
        buttonTitleMobileSuperSmall,
        buttonWaLink,
        moreInfo,
        qaValue,
        formControl: {
            hasZipError,
            hasCountyError,
            showCountySelect,
            isDialogOpen,
            setIsDialogOpen,
        },
        values: {
            zip: zipValue,
            county: countyValue,
            multiCounty: multiCountyList,
        },
        handlers: {
            zipInput: handleZipInput,
            countyChange: handleCountyChange,
            submit: handleSubmit,
            keyPress: filterKeyPress,
        },
        refs: {
            tooltip,
        },
        opts: { ...opts },
        customClasses,
        isGeneric,
    };

    return <Form {...props} />;
};

export default ZipForm;
